import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { aneelEntityParams, fetchAneelEntities } from '@/services/aneel';
import type { AneelDirectory, AneelEntityDirectoryRow } from '@/services/types';

const PAGE_SIZE = 50;

/** The four tracks, in the order the profile renders them. Same names as the API. */
const TRACKS = ['enforcement', 'ti', 'tipe', 'fees'] as const;

/**
 * The company directory: every company any ANEEL track names, by NAME.
 *
 * The way into `/aneel/e/:cnpj` for a reader who does not already hold a
 * notice or a fee row -- the due-diligence question is "is the target in here
 * at all", and until now the answer needed a case in hand. Ordered by name and
 * nothing else: a directory sorted by notice count is a table of the
 * most-sanctioned companies, which this product refuses to publish (in this
 * sector the count tracks how many installations a company operates at least
 * as much as how it behaves). There is deliberately no sort control.
 *
 * The directory panel above the list carries the measured spread -- how many
 * companies sit in one track, in all four, only in the fee ledger -- which
 * the profile's explainer once wrote into three locale files as literals.
 */
export function AneelEntitiesPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('query') ?? '');
  const [query, setQuery] = useState<string | null>(seed.get('query') || null);
  const [track, setTrack] = useState(seed.get('track') ?? '');
  const [multi, setMulti] = useState(seed.get('min_cases') === '2');
  const [exposure, setExposure] = useState(seed.get('exposure') === 'true');

  const filters = {
    query,
    track: track || null,
    min_cases: multi ? 2 : null,
    exposure: exposure || null,
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['aneel', 'entities', filters],
    queryFn: ({ pageParam }) =>
      fetchAneelEntities({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:entities.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:entities.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="aneel:entities.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path={`/api/v1/aneel/export/entities?${aneelEntityParams(filters).toString()}`}
        />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="aneel-entity-query" className="text-xs font-medium text-foreground">
            {t('aneel:entities.search.label')}
          </label>
          <input
            id="aneel-entity-query"
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('aneel:entities.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
          <p className="text-xs text-muted-foreground">{t('aneel:entities.search.hint')}</p>
        </div>
        <div className="space-y-1">
          <label htmlFor="aneel-entity-track" className="block text-xs font-medium text-foreground">
            {t('aneel:entities.filters.track')}
          </label>
          <select
            id="aneel-entity-track"
            value={track}
            onChange={(event) => setTrack(event.target.value)}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('aneel:entities.filters.any')}</option>
            {TRACKS.map((name) => (
              <option key={name} value={name}>
                {t(`aneel:entity.tracks.${name}`)}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs text-foreground">
          <input
            id="aneel-entity-multi"
            type="checkbox"
            checked={multi}
            onChange={(event) => setMulti(event.target.checked)}
          />
          {t('aneel:entities.filters.multi')}
        </label>
        <label className="flex items-center gap-2 text-xs text-foreground">
          <input
            id="aneel-entity-exposure"
            type="checkbox"
            checked={exposure}
            onChange={(event) => setExposure(event.target.checked)}
          />
          {t('aneel:entities.filters.exposure')}
        </label>
        <button
          type="submit"
          className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background"
        >
          {t('aneel:entities.search.submit')}
        </button>
      </form>

      {error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <SkeletonLoader rows={8} />
      ) : (
        <div className="space-y-4">
          <section className="rounded-lg border bg-card p-4" data-directory>
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:entities.directory.title')}
            </h2>
            <Answer answer={data.directory} help={t('aneel:entities.directory.help')}>
              {(directory) => <DirectoryPanel directory={directory} />}
            </Answer>
          </section>

          <p
            className="text-sm text-foreground"
            aria-live="polite"
            data-testid="aneel-entities-total"
            data-loaded={items.length}
            data-total={data.total}
            data-stale={String(isPlaceholderData)}
          >
            {t('common:pagination.loaded', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>

          {items.length === 0 ? (
            <EmptyState hint={t('aneel:entities.emptyHint')} />
          ) : (
            <>
              <div
                ref={scrollRef}
                className="max-h-[65vh] overflow-auto rounded-lg border"
                role="region"
                aria-label={t('aneel:entities.tableLabel')}
              >
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('aneel:entities.columns.company')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:entities.columns.cnpj')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:entities.columns.tracks')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:entities.columns.notices')}
                      </th>
                      <th className="px-3 py-2 font-medium">{t('aneel:entities.columns.years')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:entities.columns.exposure')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <DirectoryRow key={row.cnpj_digits} row={row} />
                    ))}
                  </tbody>
                </table>

                <div ref={sentinelRef} aria-hidden className="h-px" />
                <div className="flex justify-center py-3">
                  {hasNextPage ? (
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={isFetchingNextPage}
                      className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                    >
                      {isFetchingNextPage
                        ? t('common:pagination.loadingMore')
                        : t('common:pagination.loadMore', { count: PAGE_SIZE })}
                    </button>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t('common:pagination.allLoaded', { total: number.format(data.total) })}
                    </p>
                  )}
                </div>
              </div>
              <FieldGuide i18nKey="aneel:entities.guide" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DirectoryPanel({ directory }: { directory: AneelDirectory }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const tiles = [
    ['entities', directory.entities],
    ['withEnforcement', directory.with_enforcement],
    ['multi', directory.with_two_or_more_cases],
    ['feeOnly', directory.fee_only],
    ['exposure', directory.with_fee_exposure],
  ] as const;

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {tiles.map(([key, value]) => (
          <div key={key} className="rounded-md border bg-background p-3" data-directory-tile={key}>
            <dt className="text-xs text-muted-foreground">{t(`aneel:entities.directory.${key}`)}</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
              {number.format(value)}
            </dd>
          </div>
        ))}
      </dl>
      <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-directory-spread>
        {t('aneel:entities.directory.spread', {
          single: number.format(directory.single_track),
          allFour: number.format(directory.all_four_tracks),
          feeOnly: number.format(directory.fee_only),
          feeOnlyShare: percent.format(directory.fee_only_share),
        })}
      </p>
    </div>
  );
}

/**
 * One company. Presence per track as four marks, the way the case list marks
 * its four state axes; each carries its own accessible value.
 */
function DirectoryRow({ row }: { row: AneelEntityDirectoryRow }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const presence = [
    ['enforcement', row.enforcement_cases],
    ['ti', row.ti_cases],
    ['tipe', row.tipe_cases],
    ['fees', row.fee_rows],
  ] as const;

  return (
    <tr className="border-t align-top hover:bg-muted/30" data-entity-row={row.cnpj_digits}>
      <td className="px-3 py-1.5">
        <Link
          to={`/aneel/e/${row.cnpj_digits}`}
          className="line-clamp-2 font-medium text-primary underline-offset-4 hover:underline"
          title={row.canonical_name}
        >
          {row.canonical_name}
        </Link>
      </td>
      <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{row.cnpj_digits}</td>
      <td className="min-w-[10rem] px-3 py-1.5">
        <span className="grid grid-cols-2 gap-1">
          {presence.map(([name, count]) => {
            const on = count > 0;
            const label = `${t(`aneel:entity.tracks.${name}`)}: ${
              on ? number.format(count) : t('aneel:entity.tracks.absent')
            }`;
            return (
              <span
                key={name}
                title={label}
                aria-label={label}
                data-track={name}
                data-on={String(on)}
                className={
                  on
                    ? 'truncate rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-center text-[0.65rem] font-medium text-foreground'
                    : 'truncate rounded border border-dashed px-1.5 py-0.5 text-center text-[0.65rem] text-muted-foreground/60'
                }
              >
                {t(`aneel:entity.tracks.${name}`)}
              </span>
            );
          })}
        </span>
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-foreground">
        {row.enforcement_cases > 0 ? number.format(row.enforcement_cases) : ''}
      </td>
      <td className="px-3 py-1.5 tabular-nums text-muted-foreground">
        {row.first_notice_year !== null && row.last_notice_year !== null
          ? t('aneel:entities.yearSpan', { first: row.first_notice_year, last: row.last_notice_year })
          : ''}
      </td>
      <td className="px-3 py-1.5">
        {row.has_fee_exposure ? (
          <span
            className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[0.65rem] text-foreground"
            title={t('aneel:entities.exposureTitle')}
            data-fee-exposure
          >
            {t('aneel:entities.exposureShort')}
          </span>
        ) : null}
      </td>
    </tr>
  );
}
